import { useState, useEffect, useRef } from "react";
import ConfirmModal from "../components/ConfirmModal";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "./Layout";
import DateInput from "../components/DateInput";
import usePageMeta from "../hooks/usePageMeta";
import { useAppPermissions } from "../context/PermissionContext";
import ActionDropdown from "../components/ActionDropdown";
import { showSuccess, showError, showConfirm } from "../utils/dialog";
import { formatAppDate, formatAppDateTime } from "../utils/appDateTime";
import AssignRolesForm from "../components/forms/AssignRolesForm";
import AddressAutocomplete from "../components/AddressAutocomplete";
import {
  impersonateAccount,
  generateImpersonationLink,
  canImpersonateFromCurrentSession,
  isImpersonatingOtherAccount,
} from "../utils/auth";
import { MdEmail } from "react-icons/md";
import { FaLock, FaEnvelope, FaChevronLeft } from "react-icons/fa";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import API from "../api";
import { BASE_URL } from "../config";
import RichAuditsPanel from "../components/RichAuditsPanel";
import { resolveBackendUrl, accountAvatarImgSrc } from "../utils/accountAvatarUrl";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import TwoStepVerificationSection from "../components/twoFactor/TwoStepVerificationSection";
import ProfilePhotoInput from "../components/ProfilePhotoInput";
//--------//

const normalizeAccountState = (raw) => {
  const v = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  if (!v) return "active";
  if (v === "pending inactive" || v === "pendingactivation") {
    return "pending activation";
  }
  return v;
};

const normalizeAddressValue = (value) => {
  if (value && typeof value === "object") {
    return {
      street: value.street ?? value.line1 ?? "",
      address2: value.address2 ?? value.line2 ?? "",
      city: value.city ?? "",
      region: value.region ?? value.province ?? value.state ?? "",
      zip: value.zip ?? value.postal_code ?? "",
      country: value.country ?? "",
    };
  }

  return {
    street: typeof value === "string" ? value : "",
    address2: "",
    city: "",
    region: "",
    zip: "",
    country: "",
  };
};

const hasAddressValue = (address) => {
  const normalized = normalizeAddressValue(address);
  return Boolean(
    String(normalized.street || "").trim() ||
      String(normalized.address2 || "").trim() ||
      String(normalized.city || "").trim() ||
      String(normalized.region || "").trim() ||
      String(normalized.zip || "").trim() ||
      String(normalized.country || "").trim()
  );
};

const Account = ({}) => {
  usePageMeta("Account");
  const { canWriteAdmin, isSuperAdmin } = useAppPermissions();
  const canImpersonate = canImpersonateFromCurrentSession(isSuperAdmin);
  const adminReadOnly = !canWriteAdmin;

  const [selectedTab, setSelectedTab] = useState("GENERAL INFORMATION");
  const [openModal, setOpenModal] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const userName = location.state?.userName || "User Name";
  // account data passed via location.state when opening the detail page/modal
  const accountId = location.state?.accountId || location.state?.id || null;
  // Inside an impersonated session, roles can only be assigned on the impersonated user's own page
  const assignRolesBlocked = isImpersonatingOtherAccount(accountId);
  const initialState = normalizeAccountState(
    location.state &&
      (location.state.state ||
        location.state.status ||
        location.state.account_state)
  );
  const [accountState, setAccountState] = useState(initialState);
  const [actionLoading, setActionLoading] = useState(false);

  // Account form state (populated from backend)
  const [accountForm, setAccountForm] = useState({
    preferredName: "",
    firstName: "",
    lastName: "",
    middleName: "",
    userType: "",
    email: "",
    dob: "",
    gender: "",
    mobileNumber: "",
    force2fa: false,
    address: "",
    avatar_url: "",
    create_date: null,
  });
  const [securityInfo, setSecurityInfo] = useState({
    email: "",
    last_signin: null,
    two_factor_enabled: false,
    devices: [],
  });
  const [securityLoading, setSecurityLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [rawAccountResponse, setRawAccountResponse] = useState(null);
  const [accountRoles, setAccountRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [verificationMethods, setVerificationMethods] = useState([]);
  const [accountTypeOptions, setAccountTypeOptions] = useState([]);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [impersonateDialog, setImpersonateDialog] = useState({
    open: false,
    accountId: null,
  });
  const [impersonateMode, setImpersonateMode] = useState("new");
  const [impersonateBusy, setImpersonateBusy] = useState(false);
  const [impersonationCopyUrl, setImpersonationCopyUrl] = useState("");
  const [impersonationCopied, setImpersonationCopied] = useState(false);
  const [accountErrors, setAccountErrors] = useState({
    firstName: "",
    lastName: "",
    userType: "",
    email: "",
    mobileNumber: "",
    address: "",
  });

  const fileInputRef = useRef(null);
  const [avatarErr, setAvatarErr] = useState(false);

  const resolvedAvatarUrl =
    (accountForm.avatar_url && (accountForm.avatar_url.startsWith("blob:") || accountForm.avatar_url.startsWith("data:")))
      ? accountForm.avatar_url
      : (resolveBackendUrl(accountForm.avatar_url) ||
        accountAvatarImgSrc(accountId, {
          account_id: accountId,
          profile_picture: accountForm.avatar_url,
          first_name: accountForm.firstName,
          last_name: accountForm.lastName,
          image_type: rawAccountResponse?.payload?.data?.image_type || rawAccountResponse?.payload?.image_type,
          image_time: rawAccountResponse?.payload?.data?.image_time || rawAccountResponse?.payload?.image_time,
        }));

  useEffect(() => {
    setAvatarErr(false);
  }, [resolvedAvatarUrl]);

  const getAccountInitials = () => {
    const fn = String(accountForm.firstName || "").trim();
    const ln = String(accountForm.lastName || "").trim();
    if (fn || ln) {
      return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
    }
    const pref = String(accountForm.preferredName || "").trim();
    if (pref) {
      const parts = pref.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return pref.slice(0, 2).toUpperCase();
    }
    const name = String(userName || "").trim();
    if (name && name !== "User Name") {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    return "?";
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !accountId) return;

    // Instant local preview
    const localPreviewUrl = URL.createObjectURL(file);
    const previousAvatarUrl = accountForm.avatar_url;

    setAvatarErr(false);
    setAccountForm((prev) => ({
      ...prev,
      avatar_url: localPreviewUrl,
    }));

    try {
      setActionLoading(true);
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/personnel/accounts/${accountId}/avatar`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      });
      const json = await res.json();
        if (json.success && (json.url || json.avatar_url || json.profile_picture)) {
          const rawUrl = json.url || json.avatar_url || json.profile_picture;
          const timestamp = Date.now();
          const cacheBustedUrl = rawUrl.includes("?")
            ? `${rawUrl}&t=${timestamp}`
            : `${rawUrl}?t=${timestamp}`;
          setAccountForm((prev) => ({
            ...prev,
            avatar_url: cacheBustedUrl,
          }));
          showSuccess("Profile picture updated successfully");
          // Re-sync account details so metadata stays fully updated
          await loadAccount();
        } else {
        // Revert to previous avatar if server returned error
        setAccountForm((prev) => ({
          ...prev,
          avatar_url: previousAvatarUrl,
        }));
        showError(json.message || "Avatar upload failed");
      }
    } catch (err) {
      // Revert to previous avatar on exception
      setAccountForm((prev) => ({
        ...prev,
        avatar_url: previousAvatarUrl,
      }));
      showError(err?.message || "Failed to upload avatar");
    } finally {
      setActionLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const loadSecurityInfo = async () => {
    if (!accountId) return;
    try {
      setSecurityLoading(true);
      const resp = await API.get("/api/my-account/account/security", {
        params: { accountId },
      });
      const sdata = resp?.data?.data || resp?.data || {};
      setSecurityInfo({
        email: sdata.email || accountForm.email || "",
        last_signin:
          sdata.last_signin || sdata.last_signin || sdata.last_signin || null,
        two_factor_enabled: !!sdata.two_factor_enabled,
        devices: sdata.devices || [],
      });
    } catch (err) {
      // console.warn('Failed to load security info', err?.response?.data || err?.message || String(err));
    } finally {
      setSecurityLoading(false);
    }
  };

  const loadAccount = async () => {
    if (!accountId) return;
    const endpoints = [
      `/api/admin/accounts/${accountId}`,
      `/api/personnel/accounts/${accountId}`,
      `/api/accounts/${accountId}`,
      `/api/personnel/${accountId}`,
    ];
    try {
      setAccountLoading(true);
      let found = false;
      const aggregateRaw = {};
      for (const ep of endpoints) {
        try {
          const resp = await API.get(ep);
          const data =
            resp?.data?.data?.account ||
            resp?.data?.account ||
            resp?.data?.data ||
            resp?.data ||
            {};
          aggregateRaw[ep] = resp.data;
          if (data && Object.keys(data).length) {
            setRawAccountResponse({
              from: ep,
              payload: resp.data,
              aggregate: aggregateRaw,
            });

            const fetchedAvatar =
              data.avatar_url || data.avatar || data.profile_picture || "";
            const timestamp = Date.now();
            const cacheBustedAvatar = fetchedAvatar
              ? fetchedAvatar.includes("?")
                ? `${fetchedAvatar}&t=${timestamp}`
                : `${fetchedAvatar}?t=${timestamp}`
              : "";

            setAccountForm((prev) => ({
              ...prev,
              preferredName:
                data.preferred_name || data.preferredName || data.name || "",
              firstName: data.first_name || data.firstName || "",
              lastName: data.last_name || data.lastName || "",
              middleName: data.middle_name || data.middleName || "",
              userType: String(data.type || "").toLowerCase(),
              email: data.email || "",
              dob: data.date_of_birth || data.dob || "",
              gender: data.gender || "",
              mobileNumber:
                data.phone_mobile ||
                data.phoneMobile ||
                data.mobile ||
                data.mobile_number ||
                data.mobileNumber ||
                "",
              force2fa:
                data.force2fa === true ||
                data.force_2fa === 1 ||
                data.force_2fa === "1" ||
                data.force_2fa === true,
              address: normalizeAddressValue(
                data.address && typeof data.address === "object"
                  ? data.address
                  : {
                      street: data.line1 || data.street || data.address || "",
                      address2: data.line2 || data.address2 || "",
                      city: data.city || "",
                      region: data.province || data.region || "",
                      zip: data.postal_code || data.zip || "",
                      country: data.country || "",
                    }
              ),
              avatar_url:
                cacheBustedAvatar ||
                (prev.avatar_url && prev.avatar_url.startsWith("blob:")
                  ? prev.avatar_url
                  : prev.avatar_url) ||
                "",
              create_date:
                data.create_date ||
                data.createDate ||
                data.create_date ||
                null,
            }));
            const nextState = normalizeAccountState(
              data.state || data.status || data.account_state
            );
            setAccountState(nextState);
            setAvatarErr(false);
            if (nextState === "deleted") {
              showError("Account has been deleted");
              setTimeout(() => navigate("/admin/accounts"), 2000);
              return;
            }
            found = true;
            break;
          }
        } catch (innerErr) {
          aggregateRaw[ep] = {
            error:
              innerErr?.response?.data ||
              innerErr?.message ||
              String(innerErr),
          };
        }
      }
      if (!found) {
        setRawAccountResponse({
          error: "No account found",
          aggregate: aggregateRaw,
        });
      }
    } catch (err) {
      console.error("loadAccount error", err);
    } finally {
      setAccountLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
  }, [accountId]);

  // The selected tab survives switching accounts; leave ROLES if it's blocked for this account
  useEffect(() => {
    if (assignRolesBlocked && selectedTab === "ROLES") {
      setSelectedTab("GENERAL INFORMATION");
    }
  }, [assignRolesBlocked, selectedTab]);

  useEffect(() => {
    const loadAccountTypes = async () => {
      try {
        const resp = await API.get("/api/admin/account-types");
        const options = resp?.data?.data || [];
        if (Array.isArray(options) && options.length > 0) {
          setAccountTypeOptions(options);
        } else {
          setAccountTypeOptions([
            { value: "candidate", label: "Candidate" },
            { value: "fieldstaff", label: "Field Staff" },
            { value: "outsourcedstaff", label: "Outsourced Staff" },
            { value: "contact", label: "Contact" },
            { value: "officestaff", label: "Office Staff" },
            { value: "api", label: "Api" },
          ]);
        }
      } catch (_) {
        setAccountTypeOptions([
          { value: "candidate", label: "Candidate" },
          { value: "fieldstaff", label: "Field Staff" },
          { value: "outsourcedstaff", label: "Outsourced Staff" },
          { value: "contact", label: "Contact" },
          { value: "officestaff", label: "Office Staff" },
          { value: "api", label: "Api" },
        ]);
      }
    };
    loadAccountTypes();
  }, []);

  // fetch security info (two-factor, last sign-in, trusted devices) using my-account security endpoint
  useEffect(() => {
    loadSecurityInfo();
  }, [accountId, accountForm.email]);

  // fetch account roles
  useEffect(() => {
    const loadRoles = async () => {
      if (!accountId) return;
      try {
        setRolesLoading(true);
        const resp = await API.get(
          `/api/personnel/accounts/${accountId}/roles`,
        );
        const rolesData = resp?.data?.data || [];
        setAccountRoles(rolesData);
      } catch (err) {
        console.warn(
          "Failed to load account roles",
          err?.response?.data || err?.message || String(err),
        );
        setAccountRoles([]);
      } finally {
        setRolesLoading(false);
      }
    };
    loadRoles();
  }, [accountId]);

  useEffect(() => {
    const loadAvailableRoles = async () => {
      try {
        const resp = await API.get("/api/personnel/roles", {
          params: { level: "app" },
        });
        const rows = resp?.data?.data || [];
        const appOnly = Array.isArray(rows)
          ? rows.filter((r) => {
              const level = String(
                r?.level ?? r?.role_level ?? r?.context ?? r?.roleLevel ?? "app",
              )
                .trim()
                .toLowerCase();
              return level === "app";
            })
          : [];
        setAvailableRoles(appOnly);
      } catch (_) {
        setAvailableRoles([]);
      }
    };
    loadAvailableRoles();
  }, []);

  useEffect(() => {
    const loadVerificationMethods = async () => {
      if (!accountId) return;
      try {
        const resp = await API.get("/api/my-account/account/verification-methods", {
          params: { accountId },
        });
        setVerificationMethods(resp?.data?.data || []);
      } catch (_) {
        setVerificationMethods([]);
      }
    };
    loadVerificationMethods();
  }, [accountId]);

  const handleDeleteVerificationMethod = async (methodId) => {
    if (!accountId) return showError("No account selected");
    const confirmed = await showConfirm(
      "Delete Verification Method",
      "Are you sure you want to delete this verification method?"
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/account/verification-methods/${methodId}?accountId=${accountId}`,
        {
          method: "DELETE",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        throw new Error(body.message || "Failed to delete verification method");
      }
      showSuccess(body.message || "Verification method deleted");
      setVerificationMethods((prev) =>
        prev.filter((m) => String(m.verification_method_id) !== String(methodId))
      );
    } catch (e) {
      showError(e?.message || "Failed to delete verification method");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrustedDeviceAction = async (action, device) => {
    if (!accountId) return showError("No account selected");
    const deviceId = device?.trusted_device_id;
    if (!deviceId) return showError("Invalid device");
    const actionLabel = action === "signout" ? "Sign out" : "Delete";
    const confirmed = await showConfirm(
      `${actionLabel} Device`,
      `Are you sure you want to ${actionLabel.toLowerCase()} this trusted device?`
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");
      const url =
        action === "signout"
          ? `${BASE_URL}/api/my-account/account/security/device/${deviceId}/signout?accountId=${accountId}`
          : `${BASE_URL}/api/my-account/account/security/device/${deviceId}?accountId=${accountId}`;
      const res = await fetch(url, {
        method: action === "signout" ? "POST" : "DELETE",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        throw new Error(
          body.message || `Failed to ${actionLabel.toLowerCase()} device`
        );
      }
      showSuccess(body.message || `${actionLabel} successful`);
      await loadSecurityInfo();
    } catch (e) {
      showError(e?.message || `Failed to ${actionLabel.toLowerCase()} device`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrustedDevicesBulk = async (action) => {
    if (!accountId) return showError("No account selected");
    const title =
      action === "signout" ? "Sign Out All Devices" : "Delete All Devices";
    const msg =
      action === "signout"
        ? "Are you sure you want to sign out all trusted devices?"
        : "Are you sure you want to delete all trusted devices?";
    const confirmed = await showConfirm(title, msg);
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");
      const url =
        action === "signout"
          ? `${BASE_URL}/api/my-account/account/security/devices/signout-all?accountId=${accountId}`
          : `${BASE_URL}/api/my-account/account/security/devices?accountId=${accountId}`;
      const res = await fetch(url, {
        method: action === "signout" ? "POST" : "DELETE",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        throw new Error(
          body.message ||
            `Failed to ${
              action === "signout" ? "sign out" : "delete"
            } all devices`
        );
      }
      showSuccess(body.message || "Trusted devices updated");
      await loadSecurityInfo();
    } catch (e) {
      showError(
        e?.message ||
          `Failed to ${action === "signout" ? "sign out" : "delete"} all devices`
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccountChange = (field, value) => {
    setAccountForm((prev) => ({ ...prev, [field]: value }));
    if (
      field === "firstName" ||
      field === "lastName" ||
      field === "userType" ||
      field === "email" ||
      field === "mobileNumber" ||
      field === "address"
    ) {
      setAccountErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateGeneralInfoRequired = () => {
    const firstName = String(accountForm.firstName || "").trim();
    const lastName = String(accountForm.lastName || "").trim();
    const userType = String(accountForm.userType || "").trim();
    const email = String(accountForm.email || "").trim();
    const nextErrors = {
      ...accountErrors,
      firstName: firstName ? "" : "First Name is required",
      lastName: lastName ? "" : "Last Name is required",
      userType: userType ? "" : "User Type is required",
      email: email ? "" : "Email is required",
    };
    setAccountErrors(nextErrors);
    return (
      !nextErrors.firstName &&
      !nextErrors.lastName &&
      !nextErrors.userType &&
      !nextErrors.email
    );
  };

  const validateContactRequired = () => {
    const mobileNumber = String(accountForm.mobileNumber || "").trim();
    const address = hasAddressValue(accountForm.address);
    const nextErrors = {
      mobileNumber: mobileNumber ? "" : "Mobile Number is required",
      address: address ? "" : "Address is required",
    };
    setAccountErrors(nextErrors);
    return !nextErrors.mobileNumber && !nextErrors.address;
  };

  const handleSaveAccount = async () => {
    if (!accountId) return showError("No account selected");
    if (selectedTab === "GENERAL INFORMATION" && !validateGeneralInfoRequired()) {
      return showError("Please fill required GENERAL INFORMATION fields");
    }
    if (selectedTab === "CONTACT" && !validateContactRequired()) {
      return showError("Please fill required CONTACT fields");
    }
    try {
      setActionLoading(true);
      // build payload - only send fields we manage
      const payload = {
        preferred_name: accountForm.preferredName,
        first_name: accountForm.firstName,
        last_name: accountForm.lastName,
        middle_name: accountForm.middleName,
        email: accountForm.email,
        date_of_birth: accountForm.dob,
        gender: accountForm.gender,
        type: accountForm.userType || null,
        phone_mobile: accountForm.mobileNumber,
        force_2fa: accountForm.force2fa ? 1 : 0,
        avatar_url: accountForm.avatar_url,
      };
      const normalizedAddress = normalizeAddressValue(accountForm.address);
      payload.line1 = normalizedAddress.street || null;
      payload.line2 = normalizedAddress.address2 || null;
      payload.city = normalizedAddress.city || null;
      payload.province = normalizedAddress.region || null;
      payload.postal_code = normalizedAddress.zip || null;
      payload.country = normalizedAddress.country || null;
      // Save via personnel API which owns account records
      await API.put(`/api/personnel/accounts/${accountId}`, payload);
      showSuccess("Account saved successfully");
      // Refresh latest account details so cache-busted profile picture and saved form fields stay 100% in sync
      await loadAccount();
    } catch (err) {
      // console.error('Failed to save account', err);
      showError(
        err?.response?.data?.message || err.message || "Failed to save account",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveRole = async (entryId) => {
    if (!accountId) return;
    try {
      // For now, we'll remove the role from the local state
      // In a full implementation, you'd call an API to remove the specific role
      setAccountRoles((prev) =>
        prev.filter((role) => role.entryId !== entryId),
      );
      showSuccess("Role removed successfully");
    } catch (err) {
      showError("Failed to remove role");
    }
  };

  const handleAssignRoles = async (selectedRoleNames, selectedLevel) => {
    if (!accountId) return;
    try {
      setActionLoading(true);

      // Get role IDs from role names
      const resp = await API.get("/api/personnel/roles", {
        params: { level: selectedLevel || "app" },
      });
      const allRoles = resp?.data?.data || [];
      const normalize = (v) => String(v || "").trim().toLowerCase();

      // Map role names to role IDs
      const roleIds = selectedRoleNames
        .map((roleName) => {
          const role = allRoles.find(
            (r) => normalize(r.name) === normalize(roleName),
          );
          return role ? role.id : null;
        })
        .filter((id) => id !== null);

      if (selectedRoleNames.length > 0 && roleIds.length === 0) {
        throw new Error("Selected roles are not available in the database");
      }

      // Save roles via API
      await API.put(`/api/personnel/accounts/${accountId}/roles`, { roleIds });

      // Refresh roles data
      const rolesResp = await API.get(
        `/api/personnel/accounts/${accountId}/roles`,
      );
      const rolesData = rolesResp?.data?.data || [];
      setAccountRoles(rolesData);

      showSuccess("Roles assigned successfully");
      return true;
    } catch (err) {
      console.error("Failed to assign roles", err);
      showError(err?.response?.data?.message || "Failed to assign roles");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const closeImpersonateDialog = () => {
    setImpersonateDialog({ open: false, accountId: null });
    setImpersonationCopyUrl("");
    setImpersonationCopied(false);
  };

  const copyImpersonationUrl = async () => {
    if (!impersonationCopyUrl) return;
    try {
      await navigator.clipboard.writeText(impersonationCopyUrl);
      setImpersonationCopied(true);
      showSuccess("Impersonation URL copied");
    } catch (_) {
      showError("Failed to copy URL. Please copy it manually.");
    }
  };

  const confirmImpersonation = async () => {
    if (!impersonateDialog.accountId) return;
    try {
      setImpersonateBusy(true);
      if (impersonateMode === "new") {
        const link = await generateImpersonationLink(
          API,
          impersonateDialog.accountId
        );
        setImpersonationCopyUrl(link);
        setImpersonationCopied(false);
      } else {
        await impersonateAccount(API, impersonateDialog.accountId);
        closeImpersonateDialog();
      }
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || "Impersonation failed");
    } finally {
      setImpersonateBusy(false);
    }
  };

  // helper: map state to small badge classes and human label
  function statusColorClass(state) {
    switch ((state || "").toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "pending":
      case "pending_activation":
      case "pending activation":
        return "bg-yellow-100 text-yellow-800";
      case "invited":
        return "bg-blue-100 text-blue-800";
      case "inactive":
      case "deactivated":
        return "bg-gray-100 text-gray-800";
      case "deleted":
        return "hidden";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }

  // confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState({
    title: "",
    message: "",
    onConfirm: null,
    danger: false,
  });

  function openConfirm({
    title = "Confirm",
    message = "",
    onConfirm,
    danger = false,
  }) {
    setConfirmPayload({ title, message, onConfirm, danger });
    setConfirmOpen(true);
  }

  function closeConfirm() {
    setConfirmOpen(false);
    setConfirmLoading(false);
    setConfirmPayload({
      title: "",
      message: "",
      onConfirm: null,
      danger: false,
    });
  }

  // call admin API to change state
  async function changeAccountState(newState) {
    if (!accountId) return showError("No account selected");
    openConfirm({
      title: "Confirm state change",
      message: `Change account state to '${newState}'?`,
      danger: newState === "deleted",
      onConfirm: async () => {
        setConfirmLoading(true);
        setActionLoading(true);
        try {
          const token = localStorage.getItem("token");
          const authorId = localStorage.getItem("accountId");
          const res = await fetch(`${BASE_URL}/api/admin/accounts/${accountId}/state`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(authorId ? { "X-Author-Account-Id": authorId } : {}),
            },
            body: JSON.stringify({ state: newState }),
          });
          const body = await res.json();
          if (res.ok && body.success) {
            setAccountState(newState);
            showSuccess(body.message || "Account state updated");
            // If account was deleted, navigate back to accounts list
            if (newState === "deleted") {
              setTimeout(() => navigate("/admin/accounts"), 1500);
            }
          } else {
            throw new Error(body.message || "Failed to update account state");
          }
        } catch (err) {
          // console.error('changeAccountState error', err);
          showError(err.message || "Error");
        } finally {
          setActionLoading(false);
          setConfirmLoading(false);
          setConfirmOpen(false);
        }
      },
    });
  }

  async function handleSendActivationEmail() {
    if (!accountId) return showError("No account selected");
    try {
      setActionLoading(true);
      const current = (accountState || "").toLowerCase();
      if (
        current === "pending" ||
        current === "pendingactivation" ||
        current === "pending_activation" ||
        current === "pending activation" ||
        current === "invited"
      ) {
        const token = localStorage.getItem("token");
        const authorId = localStorage.getItem("accountId");
        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (authorId) headers["x-author-account-id"] = `${authorId}`;

        const tryUrls = [
          `/api/personnel/${accountId}/send-activation-email`,
          `/api/personnel/accounts/${accountId}/send-activation-email`,
          `/api/personnel/accounts/${accountId}/send-activation`,
          `/api/clients/contacts/${accountId}/send-activation`,
        ];
        const tempPassword = `Tmp${Math.random().toString(36).slice(-8)}!`;
        let ok = false;
        let lastErr = null;
        for (const url of tryUrls) {
          try {
            await API.post(url, { password: tempPassword }, { headers });
            ok = true;
            break;
          } catch (e) {
            if (e?.response?.status === 404) continue;
            lastErr = e;
            break;
          }
        }
        if (!ok) {
          if (lastErr) throw lastErr;
          throw new Error("Activation endpoint not found");
        }
        showSuccess("Activation email sent");
      } else {
        showError("Activation email can only be sent for pending/invited accounts");
      }
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || "Failed to send activation email");
    } finally {
      setActionLoading(false);
    }
  }

  const headerDisplayName =
    [accountForm.firstName, accountForm.lastName].filter(Boolean).join(" ").trim() ||
    userName;
  const headerAvatarSrc =
    resolveBackendUrl(accountForm.avatar_url) ||
    accountAvatarImgSrc(accountId, {
      account_id: accountId,
      profile_picture: accountForm.avatar_url,
      first_name: accountForm.firstName,
      last_name: accountForm.lastName,
    });

  return (
    <Layout active="Account">
      <fieldset
        disabled={adminReadOnly}
        className="border-0 m-0 p-0 min-w-0 min-h-0"
      >
      <div className="py-3 px-2 flex flex-col min-h-screen no-mobile-table-stack">
        {/* Header */}
        <header className="flex items-center justify-between ml-6 mb-4 ml-2">
          <div className="flex items-center gap-3">
            {/* Back arrow icon */}
            <FaChevronLeft
              className="text-gray-500 cursor-pointer hover:text-gray-600"
              size={20}
              onClick={() => navigate(-1)} // navigate back
            />
            {/* Profile picture circle */}
            <StaffAvatarCircle
              accountId={accountId}
              avatarSrc={headerAvatarSrc}
              employee={{
                account_id: accountId,
                profile_picture: accountForm.avatar_url,
                first_name: accountForm.firstName,
                last_name: accountForm.lastName,
                name: headerDisplayName,
              }}
              className="w-10 h-10 border-2 border-gray-300"
              textClassName="text-xs font-medium text-gray-700"
            />
            <h1 className="text-2xl font-semibold text-foreground">
              {headerDisplayName}
            </h1>
          </div>
        </header>

        <div className="h-[1px] bg-[#a15d33] mx-2 sm:mx-6"></div>

        <div className="mt-6 mb-4 mx-2 sm:mx-6 p-2 sm:p-4 border-2 border-gray-200 rounded-xl h-full">
          {/* Tabs */}
          <div className="flex space-x-4 md:space-x-8 mb-6 border-b border-[#DC1F004D] overflow-x-auto pb-1 hide-scrollbar">
            {[
              "GENERAL INFORMATION",
              "CONTACT",
              "SIGN IN & SECURITY",
              "ROLES",
              "VERIFICATION METHODS",
              "AUDITS",
            ].map((tab) => {
              const tabDisabled = tab === "ROLES" && assignRolesBlocked;
              return (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  disabled={tabDisabled}
                  title={
                    tabDisabled
                      ? "Roles are not available for other users while impersonating"
                      : undefined
                  }
                  className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    tabDisabled
                      ? "border-transparent opacity-50 cursor-not-allowed"
                      : selectedTab === tab
                        ? "border-[#DC1F00] text-sidebar-active"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* GENERAL INFORMATION section */}
          {selectedTab === "GENERAL INFORMATION" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* first column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">
                  Profile Picture
                </label>
                <div className="relative w-24 h-24 mb-4">
                  {/* Profile Circle with Teal/Emerald Gradient matching Personnel Office Staff */}
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-emerald-400 to-teal-600 border-4 border-white shadow-lg flex items-center justify-center">
                    {resolvedAvatarUrl && !avatarErr ? (
                      <img
                        key={resolvedAvatarUrl}
                        alt="Profile"
                        src={resolvedAvatarUrl}
                        className="w-full h-full object-cover"
                        onError={() => setAvatarErr(true)}
                      />
                    ) : (
                      <span className="text-2xl font-bold text-white tracking-wider">
                        {getAccountInitials()}
                      </span>
                    )}
                  </div>
                  {/* Camera Icon Overlay */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={actionLoading || adminReadOnly}
                    className="absolute bottom-0 right-0 bg-red-600 hover:bg-red-700 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer border-2 border-white shadow-md transition-colors disabled:opacity-50"
                    title="Upload profile picture"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                    </svg>
                  </button>
                  <ProfilePhotoInput
                    type="file"
                    ref={fileInputRef}
                    onChange={handleAvatarUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
                <label className="block text-sm text-[#00000080] mb-2">
                  Preffered Name
                </label>
                <input
                  type="text"
                  name="preferredName"
                  value={accountForm.preferredName}
                  onChange={(e) =>
                    handleAccountChange("preferredName", e.target.value)
                  }
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                <label className="block text-sm text-[#00000080] mb-2">
                  Date of Birth
                </label>
                <DateInput
                  name="dob"
                  value={accountForm.dob}
                  onChange={(value) => handleAccountChange("dob", value)}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* second column */}
              <div className="p-4 mt-1 lg:mt-6">
                <label className="block text-sm text-[#00000080] mb-2">
                  First Name <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={accountForm.firstName}
                  onChange={(e) =>
                    handleAccountChange("firstName", e.target.value)
                  }
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                {accountErrors.firstName && (
                  <p className="text-xs text-red-600 -mt-2 mb-3">
                    {accountErrors.firstName}
                  </p>
                )}
                <label className="block text-sm text-[#00000080] mb-2">
                  Last Name <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={accountForm.lastName}
                  onChange={(e) =>
                    handleAccountChange("lastName", e.target.value)
                  }
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                {accountErrors.lastName && (
                  <p className="text-xs text-red-600 -mt-2 mb-3">
                    {accountErrors.lastName}
                  </p>
                )}
                <label className="block text-sm text-[#00000080] mb-2">
                  Middle Name
                </label>
                <input
                  type="text"
                  name="middleName"
                  value={accountForm.middleName}
                  onChange={(e) =>
                    handleAccountChange("middleName", e.target.value)
                  }
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* third column */}
              <div className="p-4 mt-1 lg:mt-6">
                <label className="block text-sm text-[#00000080] mb-2">
                  User Type <span className="text-[#00000080]">*</span>
                </label>
                <select
                  name="userType"
                  value={accountForm.userType}
                  onChange={(e) =>
                    handleAccountChange("userType", e.target.value)
                  }
                  className="w-full border border-[#00000033] rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                >
                  <option value="">Select</option>
                  {accountTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {accountErrors.userType && (
                  <p className="text-xs text-red-600 -mt-2 mb-3">
                    {accountErrors.userType}
                  </p>
                )}
                <label className="block text-sm text-[#00000080] mb-2">
                  Email <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={accountForm.email}
                  onChange={(e) => handleAccountChange("email", e.target.value)}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                {accountErrors.email && (
                  <p className="text-xs text-red-600 -mt-2 mb-3">
                    {accountErrors.email}
                  </p>
                )}
                <label className="block text-sm text-[#00000080] mb-2">
                  Gender
                </label>
                <select
                  name="Gender"
                  value={accountForm.gender}
                  onChange={(e) =>
                    handleAccountChange("gender", e.target.value)
                  }
                  className="w-full border border-[#00000033] rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}

          {/* CONTACT section */}
          {selectedTab === "CONTACT" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
              <div>
                <label className="block text-sm text-[#00000080] mb-2">
                  Mobile Number <span className="text-[#00000080]">*</span>
                </label>
                <div className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus-within:ring-1 focus-within:ring-red-400 bg-white">
                  <PhoneInput
                    international
                    defaultCountry="CA"
                    value={accountForm.mobileNumber || ""}
                    onChange={(value) =>
                      handleAccountChange("mobileNumber", String(value || ""))
                    }
                    className="w-full"
                  />
                </div>
                {accountErrors.mobileNumber && (
                  <p className="text-xs text-red-600 -mt-2 mb-3">
                    {accountErrors.mobileNumber}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-[#00000080] mb-2">
                  Address <span className="text-[#00000080]">*</span>
                </label>
                <AddressAutocomplete
                  value={accountForm.address}
                  onChange={(addr) => handleAccountChange("address", addr)}
                  manualInitialName={accountForm.preferredName}
                  onManualSave={(name) =>
                    handleAccountChange("preferredName", name)
                  }
                />
                {accountErrors.address && (
                  <p className="text-xs text-red-600 mt-1">{accountErrors.address}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-[#00000080] mb-2">
                  Force 2FA
                </label>
                <label className="flex items-center gap-2 text-sm mb-4">
                  <input
                    type="checkbox"
                    className="accent-[#DC1F00]"
                    checked={!!accountForm.force2fa}
                    onChange={(e) =>
                      handleAccountChange("force2fa", e.target.checked)
                    }
                  />
                  <span className=" text-sm text-[#00000080]">Enabled</span>
                </label>
              </div>
            </div>
          )}

          {/* SIGN IN & SECURITY section */}
          {selectedTab === "SIGN IN & SECURITY" && (
            <div className="p-4">
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-1">Account Status</h2>

                <div className="flex items-center flex-wrap gap-2">
                  <span className="font-medium text-[#00000080]">Created:</span>
                  <span className="text-[#000000]">
                    {accountForm.create_date
                      ? formatAppDateTime(accountForm.create_date)
                      : "—"}
                  </span>

                  <span className="font-medium text-[#00000080] sm:ml-10 mt-2 sm:mt-0">
                    Account Status:
                  </span>
                  <span
                    className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-sm font-medium ${statusColorClass(accountState)}`}
                  >
                    {String(accountState)
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                  <ActionDropdown
                    actions={(() => {
                      const state = (accountState || "").toLowerCase();
                      const actions = [];

                      actions.push({
                        label: "Impersonate",
                        // No onClick renders the item disabled (Super Admins / Admins only)
                        onClick: canImpersonate
                          ? () => {
                              if (!accountId) return showError("No account selected");
                              setImpersonateDialog({ open: true, accountId });
                              setImpersonateMode("new");
                              setImpersonationCopyUrl("");
                              setImpersonationCopied(false);
                            }
                          : undefined,
                      });

                      if (state === "active") {
                        actions.push({
                          label: "Delete Account",
                          onClick: () => changeAccountState("deleted"),
                          danger: true,
                        });
                      } else if (state === "deleted") {
                        actions.push({
                          label: "Active",
                          onClick: () => changeAccountState("active"),
                        });
                        actions.push({
                          label: "Pending Activation",
                          onClick: () => changeAccountState("pending activation"),
                        });
                      } else if (
                        state === "pending" ||
                        state === "pendingactivation" ||
                        state === "pending_activation" ||
                        state === "pending activation" ||
                        state === "invited"
                      ) {
                        actions.push({
                          label: "Send Activation Email",
                          onClick: () =>
                            openConfirm({
                              title: "Confirm",
                              message:
                                "Are you sure you would like to send an activation email?",
                              onConfirm: async () => {
                                await handleSendActivationEmail();
                                closeConfirm();
                              },
                            }),
                        });
                        actions.push({
                          label: "Activate Account",
                          onClick: () => changeAccountState("active"),
                        });
                        actions.push({
                          label: "Delete Account",
                          onClick: () => changeAccountState("deleted"),
                          danger: true,
                        });
                      }

                      return actions;
                    })()}
                  />
                  <ConfirmModal
                    open={confirmOpen}
                    title={confirmPayload.title}
                    message={confirmPayload.message}
                    onConfirm={() =>
                      confirmPayload.onConfirm && confirmPayload.onConfirm()
                    }
                    onCancel={closeConfirm}
                    loading={confirmLoading}
                    danger={confirmPayload.danger}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-2 mb-6 mt-4">
                <div className="flex items-center gap-2">
                  <MdEmail className="text-[#00000080] text-lg shrink-0" />
                  <span className="font-medium text-[#00000080] shrink-0">Email:</span>
                  <span className="text-[#000000] break-all">
                    {accountForm.email || securityInfo.email || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 sm:ml-8">
                  <FaLock className="text-[#00000080] text-lg shrink-0" />
                  <span className="font-medium text-[#00000080] shrink-0">Password:</span>
                  <button
                    className="bg-[#DC1F0033] text-[#DC1F00] px-4 py-1 rounded-3xl hover:bg-[#CC2100] hover:text-white text-sm whitespace-nowrap"
                    onClick={() => setResetModalOpen(true)}
                  >
                    Reset Password
                  </button>
                </div>
              </div>
              <p className="text-[#000000] ">
                Last Sign In:{" "}
                <span className="text-[#000000] ml-2 sm:ml-6">
                  {securityInfo.last_signin
                    ? formatAppDateTime(securityInfo.last_signin, { emptyValue: "Never" })
                    : "—"}
                </span>
              </p>

              <TwoStepVerificationSection
                accountId={accountId}
                userDisplayName={`${accountForm.firstName || ""} ${accountForm.lastName || ""}`.trim()}
              />

              <div className="mt-8">
                <h2 className="text-lg font-semibold mb-2">Trusted Devices</h2>
                <p className="text-[#00000080]">Most Recent:</p>

                {securityLoading ? (
                  <p className="text-sm text-[#00000080]">Loading devices...</p>
                ) : securityInfo.devices && securityInfo.devices.length ? (
                  <div className="grid grid-cols-1 gap-2 mt-4">
                    {securityInfo.devices.map((d) => (
                      <div
                        key={
                          d.trusted_device_id ||
                          `${d.ip || ""}-${d.activity_date || ""}`
                        }
                        className="flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-4">
                          <img
                            src="/window.png"
                            alt="Device"
                            className="w-12 h-12"
                          />
                          <div>
                            <p className="font-medium">
                              {d.browser_type || d.device_type || "Device"}
                            </p>
                            <p className="text-sm text-gray-500">
                              {[d.city, d.region].filter(Boolean).join(", ")}{" "}
                              {d.country ? ` ${d.country}` : ""}
                            </p>
                            <p className="text-xs text-gray-400">
                              {d.activity_date ? formatAppDate(d.activity_date) : ""}
                            </p>
                          </div>
                        </div>
                        <ActionDropdown
                          actions={[
                            {
                              label: "Sign out",
                              onClick: () =>
                                handleTrustedDeviceAction("signout", d),
                            },
                            {
                              label: "Delete",
                              onClick: () =>
                                handleTrustedDeviceAction("delete", d),
                              danger: true,
                            },
                          ]}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#00000080]">
                    No trusted devices recorded
                  </p>
                )}

                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="button"
                    className="min-w-[160px] bg-[#DC1F00] hover:bg-[#CC2100] text-white px-4 py-2 rounded-3xl text-sm font-medium transition-colors disabled:opacity-60"
                    onClick={() => handleTrustedDevicesBulk("signout")}
                    disabled={actionLoading}
                  >
                    SIGN OUT ALL
                  </button>
                  <button
                    type="button"
                    className="min-w-[160px] border border-[#DC1F00] text-[#DC1F00] hover:bg-[#FDE9E5] px-4 py-2 rounded-3xl text-sm font-medium transition-colors disabled:opacity-60"
                    onClick={() => handleTrustedDevicesBulk("delete")}
                    disabled={actionLoading}
                  >
                    DELETE ALL
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ROLES section */}
          {selectedTab === "ROLES" && !assignRolesBlocked && (
            <div className="p-2">
              <div className="flex justify-end mb-2">
                <button
                  className={`bg-buttonRed text-white font-semibold px-4 py-2 rounded-full ${
                    assignRolesBlocked
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-buttonRed-hover"
                  }`}
                  onClick={() => setOpenModal("assignroles")}
                  disabled={assignRolesBlocked}
                  title={
                    assignRolesBlocked
                      ? "Role changes are not allowed for other users while impersonating"
                      : undefined
                  }
                >
                  ASSIGN ROLES
                </button>
                {openModal === "assignroles" && (
                  <AssignRolesForm
                    onClose={() => setOpenModal(null)}
                    onSave={handleAssignRoles}
                    currentRoles={accountRoles}
                    availableRoles={availableRoles}
                  />
                )}
              </div>

              <table className="min-w-full">
                <thead className="text-textGray text-sm border-b border-gray-400">
                  <tr className="text-left select-none">
                    <th className="text-sm font-medium px-4 py-2 w-1/4">
                      Context
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-1/4">
                      Environment
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-1/4">
                      Roles
                    </th>
                    <th className="text-sm font-medium px-3 py-2 w-[3%]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rolesLoading ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-4 text-center text-gray-500"
                      >
                        Loading roles...
                      </td>
                    </tr>
                  ) : accountRoles.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-4 text-center text-gray-500"
                      >
                        No roles assigned
                      </td>
                    </tr>
                  ) : (
                    accountRoles.map((role) => (
                      <tr
                        key={role.entryId}
                        className="border-b border-gray-400"
                      >
                        <td
                          className="px-4 py-2.5 whitespace-normal break-words cursor-pointer text-[#DC1F00]"
                          onClick={() => setOpenModal("editroles")}
                        >
                          App
                        </td>
                        {openModal === "editroles" && (
                          <EditRolesForm
                            onClose={() => setOpenModal(null)}
                            onSave={handleAssignRoles}
                            currentRoles={accountRoles}
                            availableRoles={availableRoles}
                          />
                        )}
                        <td className="px-3 py-2.5 whitespace-normal break-words">
                          Production
                        </td>
                        <td className="px-3 py-2.5 whitespace-normal break-words">
                          {role.roleName}
                        </td>
                        <td className="px-3 py-2.5 whitespace-normal break-words">
                          <ActionDropdown
                            actions={[
                              {
                                label: "Remove Role",
                                onClick: () => handleRemoveRole(role.entryId),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* VERIFICATION METHODS section */}
          {selectedTab === "VERIFICATION METHODS" && (
            <div className="p-2">
              <table className="min-w-full">
                <tbody className="divide-y divide-gray-200">
                  {verificationMethods.length === 0 ? (
                    <tr className="border-y border-gray-400">
                      <td className="px-4 py-3 text-sm text-gray-500">
                        No verification methods found
                      </td>
                    </tr>
                  ) : (
                    verificationMethods.map((m) => {
                      const isEmail = String(m.type || "").toLowerCase() === "email";
                      const label = isEmail
                        ? m.email
                        : [m.phone_country, m.phone_code, m.phone_number]
                            .filter(Boolean)
                            .join(" ");
                      return (
                        <tr
                          key={m.verification_method_id}
                          className="border-y border-gray-400"
                        >
                          <td className="px-4 py-2.5 whitespace-normal break-words">
                            <div className="flex items-center gap-2">
                              {isEmail ? (
                                <FaEnvelope className="text-textGray" />
                              ) : (
                                <span className="text-textGray">#</span>
                              )}
                              <span>{label || "-"}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 whitespace-normal break-words w-[3%]">
                            <ActionDropdown
                              actions={[
                                {
                                  label: "Delete",
                                  onClick: () =>
                                    handleDeleteVerificationMethod(
                                      m.verification_method_id
                                    ),
                                  danger: true,
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* AUDITS section */}
          {selectedTab === "AUDITS" && (
            <RichAuditsPanel
              subjectId={accountId}
              fetchUrl={
                accountId
                  ? `${BASE_URL}/api/personnel/accounts/${accountId}/audits/rich`
                  : ""
              }
              title="Audit History"
            />
          )}

          {(selectedTab === "GENERAL INFORMATION" ||
            selectedTab === "CONTACT") && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4 mb-2 sm:pl-4 mt-6">
              <button
                onClick={handleSaveAccount}
                disabled={
                  actionLoading ||
                  (selectedTab === "GENERAL INFORMATION" &&
                    (!String(accountForm.firstName || "").trim() ||
                      !String(accountForm.lastName || "").trim() ||
                      !String(accountForm.userType || "").trim() ||
                      !String(accountForm.email || "").trim())) ||
                  (selectedTab === "CONTACT" &&
                    (!String(accountForm.mobileNumber || "").trim() ||
                      !hasAddressValue(accountForm.address)))
                }
                className="w-full sm:w-[20%] bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full disabled:opacity-50"
              >
                {actionLoading ? "Saving..." : "SAVE"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!accountId) return showError("No account selected");
                  setImpersonateDialog({ open: true, accountId });
                  setImpersonateMode("new");
                  setImpersonationCopyUrl("");
                  setImpersonationCopied(false);
                }}
                disabled={!canImpersonate}
                title={
                  canImpersonate
                    ? undefined
                    : "Only Super Admins and Admins can impersonate"
                }
                className="w-full sm:w-[20%] bg-none border-2 border-[#DC1F00] text-[#DC1F00] py-2 rounded-full hover:bg-[#DC1F00] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#DC1F00]"
              >
                IMPERSONATE
              </button>
            </div>
          )}
        </div>
      </div>
      {impersonateDialog.open && (
        <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center px-4">
          <div className="bg-white rounded-md shadow-xl w-full max-w-sm p-4">
            <h3 className="text-[13px] font-semibold text-gray-800 mb-3">
              Impersonate
            </h3>

            {impersonationCopyUrl ? (
              <>
                <p className="text-[12px] text-gray-700 mb-2">
                  Warning: opening this URL in your current browser may sign out your current account.
                </p>
                <p className="text-[12px] text-gray-700 mb-3">
                  To avoid this, open an incognito/private window, then copy and paste this URL.
                </p>

                <textarea
                  readOnly
                  value={impersonationCopyUrl}
                  className="w-full h-20 p-2 text-[12px] border border-gray-300 rounded-md bg-gray-50 text-gray-800 resize-none"
                  onFocus={(e) => e.target.select()}
                />

                <div className="flex items-center gap-5 mt-3">
                  <button
                    type="button"
                    className="text-[#DC1F00] text-[13px] font-semibold tracking-wide"
                    onClick={copyImpersonationUrl}
                  >
                    {impersonationCopied ? "COPIED" : "COPY URL"}
                  </button>
                  <button
                    type="button"
                    className="text-gray-800 text-[13px] font-semibold tracking-wide"
                    onClick={closeImpersonateDialog}
                    disabled={impersonateBusy}
                  >
                    CLOSE
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  <label className="flex items-center gap-2 text-[13px] text-gray-800 cursor-pointer">
                    <input
                      type="radio"
                      name="impersonate-mode"
                      checked={impersonateMode === "new"}
                      onChange={() => setImpersonateMode("new")}
                      className="h-3.5 w-3.5 accent-[#DC1F00]"
                    />
                    New window
                  </label>
                  <label className="flex items-center gap-2 text-[13px] text-gray-800 cursor-pointer">
                    <input
                      type="radio"
                      name="impersonate-mode"
                      checked={impersonateMode === "same"}
                      onChange={() => setImpersonateMode("same")}
                      className="h-3.5 w-3.5 accent-[#DC1F00]"
                    />
                    Same window
                  </label>
                </div>

                <div className="flex items-center gap-5">
                  <button
                    type="button"
                    className="text-[#DC1F00] text-[13px] font-semibold tracking-wide disabled:opacity-50"
                    onClick={confirmImpersonation}
                    disabled={impersonateBusy}
                  >
                    {impersonateBusy ? "PLEASE WAIT..." : "CONTINUE"}
                  </button>
                  <button
                    type="button"
                    className="text-gray-800 text-[13px] font-semibold tracking-wide"
                    onClick={closeImpersonateDialog}
                    disabled={impersonateBusy}
                  >
                    CANCEL
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <ResetPasswordModal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        person={{ id: accountId }}
        email={accountForm.email || securityInfo.email || ""}
      />
      </fieldset>
      {/* Debug panel */}
      {/* <div style={{ padding: 12, margin: 12, border: '1px dashed #ccc', background: '#fff' }}>
      <h3 style={{ margin: 0, marginBottom: 8 }}>DEBUG: Account load</h3>
      <div style={{ fontSize: 13, color: '#333' }}>
        <div><strong>accountId:</strong> {String(accountId || '')}</div>
        <div style={{ marginTop: 8 }}><strong>accountForm:</strong></div>
        <pre style={{ maxHeight: 200, overflow: 'auto', background: '#f7f7f7', padding: 8 }}>{JSON.stringify(accountForm, null, 2)}</pre>
        <div style={{ marginTop: 8 }}><strong>rawAccountResponse:</strong></div>
        <pre style={{ maxHeight: 300, overflow: 'auto', background: '#f0f0f0', padding: 8 }}>{JSON.stringify(rawAccountResponse, null, 2)}</pre>
      </div>
    </div> */}
    </Layout>
  );
};

export default Account;

function ResetPasswordModal({ person, isOpen, onClose, email }) {
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [generated, setGenerated] = useState("");
  const [custom, setCustom] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [forceChange, setForceChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const generatePassword = (len = 12) => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const syms = "!@#$%^&*()-_=+[]{}<>?";
    const all = lower + upper + nums + syms;
    let out = "";
    out += lower[Math.floor(Math.random() * lower.length)];
    out += nums[Math.floor(Math.random() * nums.length)];
    out += upper[Math.floor(Math.random() * upper.length)];
    out += syms[Math.floor(Math.random() * syms.length)];
    for (let i = out.length; i < len; i++) {
      out += all[Math.floor(Math.random() * all.length)];
    }
    return out
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  useEffect(() => {
    if (isOpen) {
      setSendEmail(true);
      setAutoGenerate(true);
      setForceChange(true);
      setGenerated(generatePassword(12));
      setCustom("");
      setShowPassword(false);
      setError("");
    }
  }, [isOpen, person?.id]);

  const validatePassword = (p) => {
    const errs = [];
    if (!p || p.length < 8) errs.push("At least 8 characters");
    if (!/[a-z]/.test(p)) errs.push("One lowercase letter");
    if (!/[A-Z]/.test(p)) errs.push("One uppercase letter");
    if (!/[0-9]/.test(p)) errs.push("One number");
    if (!/[!@#$%^&*()\-_=+\[\]{}<>?]/.test(p))
      errs.push("One special character");
    return errs;
  };

  const handleReset = async () => {
    const password = autoGenerate ? generated : custom;
    if (!person?.id) return showError("No account selected");
    if (!password) return setError("Password is required");
    if (!autoGenerate) {
      const errs = validatePassword(password);
      if (errs.length) return setError(errs.join(", "));
    }
    try {
      setSaving(true);
      setError("");
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/personnel/${person.id}/reset-password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            password,
            email: email || null,
            send_email: !!sendEmail,
            force_change_on_next_signin: !!forceChange,
            auto_generated: !!autoGenerate,
          }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to reset password");
      }
      showSuccess(json.message || "Password reset successfully");
      onClose();
    } catch (e) {
      setError(e.message || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6">
        <div className="text-lg font-semibold">Reset Password</div>
        <div className="text-sm text-gray-500 mt-1">
          Set a new password and optionally email it to staff.
        </div>
        <div className="mt-4 space-y-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
            />
            Auto-generate password
          </label>
          <div>
            <input
              type={showPassword ? "text" : "password"}
              className="w-full border rounded px-3 py-2"
              value={autoGenerate ? generated : custom}
              onChange={(e) =>
                autoGenerate ? setGenerated(e.target.value) : setCustom(e.target.value)
              }
            />
            <label className="inline-flex items-center gap-2 text-xs mt-2">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
              Show password
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            Send password by email
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={forceChange}
              onChange={(e) => setForceChange(e.target.checked)}
            />
            Force password change on next sign-in
          </label>
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 bg-[#DC1F00] text-white rounded disabled:opacity-50"
          >
            {saving ? "PLEASE WAIT..." : "RESET PASSWORD"}
          </button>
        </div>
      </div>
    </div>
  );
}

//Edit roles form
function EditRolesForm({
  onClose,
  onSave,
  currentRoles = [],
  availableRoles = [],
}) {
  const [selectedRoles, setSelectedRoles] = useState(
    currentRoles.map((r) => r.roleName),
  );

  const roles = Array.isArray(availableRoles)
    ? availableRoles.map((r) => r.name).filter(Boolean)
    : [];

  // Handle checkbox toggle
  const handleCheckboxChange = (role) => {
    if (selectedRoles.includes(role)) {
      // Remove role if already selected
      setSelectedRoles(selectedRoles.filter((r) => r !== role));
    } else {
      // Add role if not selected
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const handleSave = async () => {
    if (!onSave) return onClose();
    const ok = await onSave(selectedRoles, "app");
    if (ok !== false) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
        >
          ✖
        </button>
        <h2 className="text-xl text-[#3A4149] font-semibold mb-4">
          Edit Roles
        </h2>
        <div className="h-[1px] bg-[#a15d33]"></div>

        <div className="flex-1 px-6 pt-6">
          <label className="block text-sm text-[#00000080] mb-2">
            Roles<span className="text-textGray">*</span>
          </label>
          <div className="space-y-2 mb-6">
            {roles.length === 0 ? (
              <p className="text-sm text-gray-500">No roles available</p>
            ) : roles.map((role) => (
              <label
                key={role}
                className="flex items-center text-base cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role)}
                  onChange={() => handleCheckboxChange(role)}
                  className="w-4 h-4 accent-[#DC1F00] mr-2"
                />
                {role}
              </label>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4">
            <button
              onClick={onClose}
              className="w-full border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] px-4 py-2 rounded-full"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="w-full bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 font-semibold rounded-full"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  ); 
}
